import React, { useState } from 'react'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import styles from 'styles/components/StripePaymentForm.module.sass'
import { useToast } from 'provider/ToastProvider'
import StripeError from 'interface/StripeError'

const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
if (!publishableKey) {
    throw new Error('Stripe publishable key is not set.')
}

interface SubscriptionSetupFormProps {
    setupIntentSecret: string;
    onPaymentSuccess: (paymentMethodId: string) => void;
    onPaymentError: (errorMessage: string) => void;
    onPaymentCancel: () => void;
}

/**
 * Props for the SubscriptionSetupForm component.
 * @component SubscriptionSetupForm
 * 
 * @param {Object} props - Props for the component.
 * @param {string} setupIntentSecret - The client secret obtained from the Stripe API for confirming the setup intent.
 * @param {(paymentMethodId: string) => void} onPaymentSuccess - Callback triggered when the payment method is successfully set up, receiving the payment method ID as a parameter.
 * @param {(errorMessage: string) => void} onPaymentError - Callback triggered when there is an error during the setup process, receiving an error message as a parameter.
 * @param {() => void} onPaymentCancel - Callback triggered when the payment setup is cancelled by the user.
 *
 * @example
 * <SubscriptionSetupForm
 *  setupIntentSecret="seti_1HxxxxxxSecretKey"
 *  onPaymentSuccess={(paymentMethodId) => console.log("Payment method set up with ID:", paymentMethodId)}
 *  onPaymentError={(errorMessage) => console.error("Payment setup failed with error:", errorMessage)}
 *  onPaymentCancel={() => console.log("Payment setup cancelled by user")}
 * />
 * 
 * @returns {JSX.Element} SubscriptionSetupForm component
 */
const SubscriptionSetupForm: React.FC<SubscriptionSetupFormProps> = ({ setupIntentSecret, onPaymentSuccess, onPaymentError, onPaymentCancel }) => {
    const { addToast } = useToast()
    const stripe = useStripe();
    const elements = useElements();

    const [failedAttempts, setFailedAttempts] = useState<number>(0)
    const [showFreePlanOption, setShowFreePlanOption] = useState<boolean>(false)
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        if (!stripe || !elements) {
            onPaymentError('Stripe has not loaded yet. Please try again later.')
            return;
        }

        if (!setupIntentSecret || !setupIntentSecret.startsWith('seti_')) {
            onPaymentError('Invalid payment setup details.')
            return;
        }

        setIsSubmitting(true)
        try {
            const cardElement = elements.getElement(CardElement)
            if (!cardElement) {
                onPaymentError('CardElement not found.')
                return;
            }

            // Check if Setup Intent was alrady confirmed.
            const { error: retrieveError, setupIntent: existingSetupIntent } = await stripe.retrieveSetupIntent(setupIntentSecret)

            if (retrieveError) {
                onPaymentError(retrieveError.message || 'Error retrieving payment setup status.')
                return;
            }

            let paymentMethodId: string | undefined;

            if (existingSetupIntent?.status === 'succeeded') {
                paymentMethodId = existingSetupIntent.payment_method as string;
            } else {

                // Step 1: Confirm Setup Intent - save card, get PM id.
                const { error: siErr, setupIntent: newSetupIntent } = await stripe.confirmCardSetup(setupIntentSecret, {
                    payment_method: { card: cardElement },
                })

                if (siErr) {
                    const stripeError = siErr as StripeError || Error
                    const errorMessage =
                        stripeError.decline_code ||
                        stripeError.code ||
                        stripeError.message ||
                        'Unexpected error while processing payment.'

                    onPaymentError(errorMessage)
                    return;
                }
                paymentMethodId = newSetupIntent?.payment_method as string;
            }

            if (!paymentMethodId) {
                throw new Error('Failed to setup payment method.')
            }

            // Card confirmed — notify parent to call the appropriate v2 endpoint.
            onPaymentSuccess(paymentMethodId)
        } catch (error) {
            const stripeError = error as StripeError || Error
            const errorMessage =
                stripeError.decline_code ||
                stripeError.code ||
                stripeError.message ||
                'Unexpected error while processing payment.'

            onPaymentError(errorMessage)

            // Increment failed attempts
            setFailedAttempts((prev) => {
                const newCount = prev + 1

                // After 5 failed attempts, cancel the payment.
                if (newCount >= 5) {
                    addToast({
                        type: 'error',
                        message: 'Payment failed after multiple attempts. Please try again later.',
                    })
                    onPaymentCancel?.()
                } else if (newCount === 3) {
                    addToast({
                        type: 'warning',
                        message: `You have made ${failedAttempts + 1} unsuccessful payment attempts. Please check your payment details.`,
                    })
                    setShowFreePlanOption(true)
                }
                return newCount
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleSubmitWrapper = (event: React.FormEvent) => {
        void handleSubmit(event)
    }

    const handleCancelPayment = () => {
        setShowFreePlanOption(false);
        onPaymentCancel()
    }

    const cardElementOptions = {
        style: {
            base: {
                width: '100%',
                height: 'auto',
                minHeight: 'auto',
                fontSize: '18px',
                color: '#424770',
                letterSpacing: '0.025em',
                fontFamily: 'Poppins, sans-serif',
                padding: '10px',
                '::placeholder': {
                    color: '#aab7c4',
                },
            },
            invalid: {
                color: '#9e2146',
            },
        },
    };

    return (
        <form onSubmit={handleSubmitWrapper} className={styles['payment-form']}>
            <div className={styles['card-element-container']}>
                <CardElement options={cardElementOptions} />
            </div>
            <button
                type="submit"
                className={styles['submit-button']}
                disabled={!stripe || isSubmitting}
            >
                {isSubmitting ? 'Processing...' : 'Save Card & Continue'}
            </button>

                {/** Free plan option after 3 failed attempts */}
                {showFreePlanOption && (
                    <div className={styles['free-plan-option']}>
                        <p className={styles['payment-message']}>{`Having trouble. You can try another card or switch to the free plan.`}</p>
                        <button
                            type='button'
                            className={styles['free-plan-button']}
                            onClick={handleCancelPayment}    
                        >
                            {'Cancel Payment'}
                        </button>
                    </div>
                )}
        </form>
    )
}

export default SubscriptionSetupForm;
